import React from "react";
import { createNotificationInbox } from "@thekrystalship/krystal/lib/notificationInbox";
import { createNotificationsClient } from "@thekrystalship/krystal/lib/notificationsClient";
import { createStore, useStore } from "@thekrystalship/krystal/lib/store";
import { api } from "./apiClient.js";
import { hostsStore } from "./stores.js";

// notifications.js — the panel's notifications: each connected node tells its own people about its own
// fleet, and the tray shows every node's history in one list.
//
// A node's routes are its own (`/api/v1/me/notifications…`), signed with the one session and aimed at
// that node by id, so there is one client and one inbox per node. `mergedInbox` presents them to the
// design system's tray as a single inbox: rows in time order with the node named in each, unread summed
// over the nodes, and a row marked read at the node that holds it.

const nodes = new Map(); // hostId -> { client, inbox }

/** The notifications client for one node. Stable across calls, so a component may depend on it. */
function notificationsClientFor(hostId) {
  return nodeFor(hostId).client;
}

function nodeFor(hostId) {
  let node = nodes.get(hostId);
  if (!node) {
    const scoped = () => api.host(hostId);
    const client = createNotificationsClient({
      request: (method, path, body) => {
        const route = "/me" + path;
        if (method === "GET") return scoped().get(route);
        if (method === "POST") return scoped().post(route, body);
        if (method === "PUT") return scoped().put(route, body);
        return scoped().delWith(route, body);
      },
    });
    node = { client, inbox: createNotificationInbox(client) };
    nodes.set(hostId, node);
  }
  return node;
}

const hostName = (hostId) => {
  const host = hostsStore.find(hostId);
  return (host && host.name) || hostId;
};

const byNewest = (a, b) => (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0);

function createMergedInbox() {
  const store = createStore({ unread: 0, items: null, more: false, kinds: [], error: null });
  const held = new Map(); // hostId -> release of that node's watch
  const owner = new Map(); // item id -> hostId, for routing a mark
  let watchers = 0;
  let stopHosts = null;

  const hostIds = () => hostsStore.getState().list.map((h) => h.id);

  function recompute() {
    const states = hostIds().map((id) => [id, nodeFor(id).inbox.store.getState()]);
    const read = states.filter(([, s]) => s.items !== null);
    owner.clear();
    const items = read.flatMap(([id, s]) => s.items.map((n) => {
      owner.set(n.id, id);
      return { ...n, detail: [hostName(id), n.detail].filter(Boolean).join(" · ") };
    })).sort(byNewest);
    const kinds = new Map();
    for (const [, s] of states) for (const k of s.kinds) if (!kinds.has(k.id)) kinds.set(k.id, k);
    const failed = states.find(([, s]) => s.error);
    store.setState({
      unread: states.reduce((sum, [, s]) => sum + s.unread, 0),
      items: states.length === 0 || read.length > 0 ? items : null,
      more: states.some(([, s]) => s.more),
      kinds: [...kinds.values()],
      error: read.length === 0 && failed ? failed[1].error : null,
    });
  }

  // Watches exactly the nodes that are connected now, and follows the set as it changes.
  function reconcile() {
    const wanted = new Set(hostIds());
    for (const [id, release] of held) {
      if (!wanted.has(id)) { release(); held.delete(id); }
    }
    for (const id of wanted) {
      if (!held.has(id)) {
        const node = nodeFor(id);
        const unsubscribe = node.inbox.store.subscribe(recompute);
        const release = node.inbox.watch();
        held.set(id, () => { unsubscribe(); release(); });
      }
    }
    recompute();
  }

  function watch() {
    if (watchers++ === 0) {
      reconcile();
      stopHosts = hostsStore.subscribe(reconcile);
    }
    let live = true;
    return () => {
      if (!live) return;
      live = false;
      if (--watchers > 0) return;
      stopHosts();
      for (const release of held.values()) release();
      held.clear();
    };
  }

  async function markRead(ids) {
    const targets = ids
      ? [...new Set(ids.map((id) => owner.get(id)).filter(Boolean))]
      : hostIds();
    await Promise.all(targets.map((hostId) => nodeFor(hostId).inbox.markRead(
      ids ? ids.filter((id) => owner.get(id) === hostId) : undefined)));
  }

  return {
    useInbox() {
      React.useEffect(watch, []);
      return useStore(store);
    },
    watch,
    refresh: () => Promise.all(hostIds().map((id) => nodeFor(id).inbox.refresh())).then(() => {}),
    markRead,
    loadMore: () => Promise.all(hostIds()
      .filter((id) => nodeFor(id).inbox.store.getState().more)
      .map((id) => nodeFor(id).inbox.loadMore())).then(() => {}),
    store,
  };
}

const mergedInbox = createMergedInbox();

export { mergedInbox, notificationsClientFor };
